// Short labels for backend category keys (shared between DocumentCenterPage and ProvideDocumentModal)

import { DOCUMENT_CATEGORY_LABELS, DOCUMENT_REQUEST_CATEGORY } from '../constants'

export const getCategoryDisplayLabel = (categoryKey) =>
  DOCUMENT_CATEGORY_LABELS[categoryKey] ?? categoryKey ?? 'Document request'

/** Normalize API request status for comparisons (e.g. NEED MORE INFO → NEED_MORE_INFO). */
export const normalizeDocumentRequestStatus = (status) =>
  String(status ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')

/**
 * Upload history / workflow label: NEED_MORE_INFO → Incomplete, COMPLETED → Completed, else In Review.
 * No request (orphan uploads): em dash.
 */
export const getDocumentRequestHistoryStatusLabel = (request) => {
  if (!request) return '—'
  const n = normalizeDocumentRequestStatus(request.status)
  if (n === 'NEED_MORE_INFO') return 'Incomplete'
  if (n === 'COMPLETED') return 'Completed'
  return 'In Review'
}

/**
 * Required actions row (status-only for Incomplete; uploads + due fields otherwise).
 * NEED_MORE_INFO → Incomplete. If employee has uploaded → Submitted. Else due date / Due ASAP.
 */
export const getDocumentRequestRequiredActionStatusLabel = (action, hasUploads) => {
  if (!action) return 'Due ASAP'
  if (normalizeDocumentRequestStatus(action.status) === 'NEED_MORE_INFO') return 'Incomplete'
  if (hasUploads) return 'Submitted'
  return action.dueDate
    ? new Date(action.dueDate).toLocaleDateString()
    : action.dueText ?? (action.due ? `Due ${action.due}` : 'Due ASAP')
}

/** True when status is NEED_MORE_INFO (Incomplete in UI). Uses request status only. */
export const documentRequestNeedsMoreInfo = (action) =>
  Boolean(action && normalizeDocumentRequestStatus(action.status) === 'NEED_MORE_INFO')

export const ENROLLMENT_API_ERROR_WHEN_NO_ERROR = 'Something went wrong. Please try again.'

export const ENROLLMENT_API_ERROR_FALLBACK = 'Request failed. Please try again.'

const ENROLLMENT_API_GENERIC_FALLBACK_MESSAGES = [
  ENROLLMENT_API_ERROR_WHEN_NO_ERROR,
  ENROLLMENT_API_ERROR_FALLBACK,
]

/** True when `message` is one of the generic fallbacks from `getEnrollmentApiErrorMessage` (not server text). */
export const isEnrollmentApiGenericFallbackMessage = (message) =>
  ENROLLMENT_API_GENERIC_FALLBACK_MESSAGES.includes(message)

export const getEnrollmentApiErrorMessage = (err) => {
  if (!err) return ENROLLMENT_API_ERROR_WHEN_NO_ERROR
  const data = err.response?.data
  if (typeof data === 'string' && data.trim()) return data.trim()
  if (Array.isArray(data?.message)) {
    const parts = data.message.filter((m) => typeof m === 'string' && m.trim())
    if (parts.length) return parts.join(' ')
  }
  if (data && typeof data.message === 'string' && data.message.trim()) return data.message.trim()
  if (typeof data?.error === 'string' && data.error.trim()) return data.error.trim()
  if (typeof err.message === 'string' && err.message.trim()) return err.message.trim()
  return ENROLLMENT_API_ERROR_FALLBACK
}

/** True when the document request workflow is fully completed (hidden from required actions). */
export const isDocumentRequestCompleted = (request) => {
  if (!request) return false
  return normalizeDocumentRequestStatus(request.status) === 'COMPLETED'
}

const DOCUMENT_REQUEST_CATEGORIES_HIDDEN_FROM_REQUIRED_ACTIONS = new Set([
  DOCUMENT_REQUEST_CATEGORY.MEDICARE_AGE_IN,
  DOCUMENT_REQUEST_CATEGORY.INDIVIDUAL_PLAN_NOT_SURECO_MANAGED,
  DOCUMENT_REQUEST_CATEGORY.SYSTEM_GENERATED,
])

export const getDocumentRequestCategoryKey = (request) =>
  request?.category ?? request?.documentType ?? request?.title ?? request?.name ?? null

/** True when the request should not appear in the Required Actions panel (employee-facing). */
export const isDocumentRequestHiddenFromRequiredActions = (request) => {
  const categoryKey = getDocumentRequestCategoryKey(request)
  return (
    categoryKey != null &&
    DOCUMENT_REQUEST_CATEGORIES_HIDDEN_FROM_REQUIRED_ACTIONS.has(String(categoryKey))
  )
}
